/* eslint-disable react/no-multi-comp */

import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Papa from 'papaparse';
import './insights.scss';

import connect from '@app/utils/connect';

import overrideFilters from '@app/actions/media/overrideFilters';
import sendNotification from '@app/actions/notifications/send';
import combineActions from '@app/utils/combineActions';

import * as reactColumns from './reacttable-columns';
import downloadCSV from '@app/utils/downloadCsv';
import { buildInsightsCsvFilterRows } from '@app/utils/csvFilterSummary/buildInsightsCsvFilterRows';
import { prependFilterSectionToCsv } from '@app/utils/csvFilterSummary/prependFilterSectionToCsv';

import { PREMIUM_SMP_BELOW_THIRTY_PERCENT } from '@app/constants/config';

import { METRIC_TYPES, SORTING_ORDER } from '@app/constants/insights';

import { DATA_COMPARING_PAGE_SIZE } from './InsightsChart';

import ErrorBoundary from '@app/ErrorBoundary';
import CrashedComponent from '@app/components/errors/CrashedComponent';
import NoDataMessage from './noDataMessage';

import getRowFilters from './getRowFilters';
import SelectingMetrics from './SelectingMetrics';
import { Client, User } from '@app/types/api';
import { useNavigate } from 'react-router-dom';
import * as format from '@app/utils/formatters';

import {
    Table,
    Typography,
    Card,
    theme,
    Dropdown,
    Space,
    Button,
    MenuProps,
    Flex,
    Skeleton,
    Tag,
    Col,
    Row,
} from '@app/ui';

import ResizableTable from '@app/components/common/ResizableTable';
import { useInsightsContext } from './InsightsContext';
import { Notification } from '@app/components/notifications/Notifications';

type InsightsProps = {
    user: User;
    client: Client;
    filters: any;
    overrideFilters: (filters: any, overridingFiltersElement?: any[]) => void;
    sendNotification: (
        message: string,
        config?: Notification['options'],
    ) => void;
};

const Insights = ({
    user,
    client,
    filters,
    overrideFilters,
}: InsightsProps) => {
    const { t } = useTranslation(['measurement', 'common']);
    const navigate = useNavigate();
    const cardRef = React.useRef(null);
    const antdTable = React.useRef(null);
    const {
        setColumns,
        query,
        insights,
        showBroadcastData,
        currency,
        total,
        compareTotal,
        compareMetric,
        hasCompareDates,
        compareInsights,
        isLoading,
        permissions,
        platformIds,
        columns,
        dateRange,
        compareDateRange,
        loading,
        isCompareLoading,
        setIsSelectingFromTable,
        setSelectedMetric,
        setSortingOrder,
        setPage,
        page,
        hasTooManyRows,
        hasTooManyRowsForCompare,
        data,
        compareValues,
        setTableSelected,
        insightsColumns,
        isSelectingFromTable,
        tableSelected,
        selectedMetric,
        yAxisMap,
        isSelectingColumns,
        setIsSelectingColumns,
    } = useInsightsContext();

    const [isHoveringTableLink, setIsHoveringTableLink] = useState(null);

    const _openSelectingColumns = e => {
        e.preventDefault();
        e.stopPropagation();
        setIsSelectingColumns(true);
    };

    const _downloadAntdTableCsv = () => {
        const { csvDelimiter: delimiter } = user;
        const { dataSource, columns } = antdTableProps;

        if (!dataSource || !columns) return;

        const columnTitles = columns.map(col => col.title as string);

        const rows = dataSource.map(row => {
            const result: Record<string, unknown> = {};
            columns.forEach(col => {
                const accessor = col.dataIndex;
                const header = col.title as string;
                let value = row[accessor];

                // Use csvFormat for export when available (e.g. group-by tag columns)
                const csvFormat = (
                    col as typeof col & {
                        csvFormat?: (value: any, row?: any) => string;
                    }
                ).csvFormat;
                if (typeof csvFormat === 'function') {
                    value = csvFormat(value, row);
                } else if (typeof col.render === 'function') {
                    // Apply render function if it exists (and isn't just JSX)
                    try {
                        const rendered = col.render(value, row);
                        if (
                            typeof rendered === 'string' ||
                            typeof rendered === 'number'
                        ) {
                            value = rendered;
                        }
                    } catch {
                        // fallback to raw value
                    }
                }

                result[header] = value;
            });
            return result;
        });

        const csv = Papa.unparse(rows, {
            delimiter,
            columns: columnTitles,
            header: true,
        });
        const filterDataRows = buildInsightsCsvFilterRows({
            filters,
            query,
            hasCompareDates,
            t,
        });
        const activeFiltersLabel = t(
            'dataExplorer.csvExport.activeFiltersLabel',
        );
        const filterRows = [[activeFiltersLabel, ''], ...filterDataRows];
        const csvWithFilters = prependFilterSectionToCsv(
            csv,
            filterRows,
            delimiter,
        );
        downloadCSV(csvWithFilters, 'insights_export');
    };

    const _showRelevant = destination => row => {
        const { id: clientId } = client;
        const { filters: newFilters, overridingFiltersElement } = getRowFilters(
            row._original,
            query?.groupBy,
            filters,
        );

        overrideFilters(newFilters, overridingFiltersElement);
        navigate(`/c/${clientId}/${destination}`);
    };

    const _setColumns = async col => {
        setColumns(col);
        setIsSelectingColumns(false);
        // Store columns setting
        localStorage.setItem(
            `mvc-insights-columns--${user.id}-${client.id}`,
            JSON.stringify(col),
        );
    };

    const groupByTable = reactColumns.getGroupByTable(key =>
        t(key, { ns: 'common' }),
    );

    const groupColumns = (query?.groupBy || [])
        .filter(p => p)
        .reduce((acc, opt) => {
            let column = groupByTable[opt];
            if (acc.length === 0) {
                column = {
                    ...column,
                    Footer: <div>Total</div>,
                };
            }
            return acc.concat(column);
        }, []);

    const currenciedHeaders = useMemo(() => {
        if (!insights) return [];
        return reactColumns
            .buildHeaders({
                currency,
                total,
                relevantPostsFn: _showRelevant('social-media-posts'),
                relevantBroadcastsFn: _showRelevant('broadcasts'),
                isAllowedPostSMPBelow30: permissions.includes(
                    PREMIUM_SMP_BELOW_THIRTY_PERCENT,
                ),
                showBroadcastData,
                t,
            })
            .filter(({ id }) => id === 'redirect' || (columns && columns[id]));
    }, [
        columns,
        currency,
        insights,
        permissions,
        platformIds,
        showBroadcastData,
        t,
    ]);

    const compareCurrenciedHeaders = useMemo(() => {
        if (!hasCompareDates || !compareInsights) return [];
        return reactColumns.buildCompareHeaders({
            currency,
            total: compareTotal,
            metric: compareMetric,
            dateRange,
            compareDateRange,
            t,
        });
    }, [currency, compareInsights, compareMetric, hasCompareDates, t]);

    const renderColumns = groupColumns.concat(currenciedHeaders);
    const renderCompareColumns = groupColumns.concat(compareCurrenciedHeaders);

    const LONG_ACCESSORS = [
        'gameName',
        'rightsholderPlacementName',
        'rightsholderName',
        'placementTypeName',
        'accountUsername',
        'collectionName',
        'postType',
        'authorshipTypeName',
        'collaboratingAccounts',
    ];

    const personalizedWidths = (accessor: string) => {
        if (accessor === 'redirect') return 40;
        if (
            accessor === 'averageMvp' ||
            accessor === 'postCount' ||
            accessor === 'exposures' ||
            accessor === 'duration' ||
            accessor === 'broadcastCount'
        )
            return 90;
        if (LONG_ACCESSORS.includes(accessor)) return 200;
        return 125;
    };

    const antdTableProps = useMemo(() => {
        const columnsRaw = hasCompareDates
            ? isCompareLoading
                ? []
                : renderCompareColumns
            : isLoading
              ? []
              : renderColumns;

        const columns = columnsRaw.map(
            c => {
                const {
                    Header,
                    accessor,
                    format: formatter,
                    id,
                    csvFormat,
                } = c as typeof c & {
                    csvFormat?: (value: any, row?: any) => string;
                };
                return {
                    title: Header,
                    dataIndex: accessor,
                    key: accessor,
                    csvFormat,

                    ellipsis: LONG_ACCESSORS.includes(accessor),
                    width: personalizedWidths(
                        id === 'redirect' ? 'redirect' : accessor,
                    ),
                    fixed: id === 'redirect' ? 'right' : false,
                    sorter:
                        id !== 'redirect'
                            ? (a, b) => {
                                  const aValue = a?.[accessor];
                                  const bValue = b?.[accessor];

                                  if (
                                      aValue === undefined ||
                                      bValue === undefined
                                  ) {
                                      return 0;
                                  }
                                  if (typeof aValue === 'string') {
                                      return aValue.localeCompare(bValue);
                                  }
                                  return aValue - bValue;
                              }
                            : undefined,
                    defaultSortOrder:
                        (isSelectingFromTable && id === tableSelected) ||
                        (!isSelectingFromTable && id === selectedMetric)
                            ? 'descend'
                            : undefined,
                    sortDirections: ['descend', 'ascend'],
                    render: (value, record) => {
                        if (id === 'redirect') {
                            const _showRelevant = destination => () => {
                                const { id: clientId } = client;
                                const {
                                    filters: newFilters,
                                    overridingFiltersElement,
                                } = getRowFilters(
                                    record,
                                    query?.groupBy,
                                    filters,
                                );

                                overrideFilters(
                                    newFilters,
                                    overridingFiltersElement,
                                );
                                navigate(`/c/${clientId}/${destination}`);
                            };

                            const { broadcastCount, postCount } = record ?? {};

                            const items: MenuProps['items'] = [
                                {
                                    key: '1',
                                    label: (
                                        <a
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={_showRelevant(
                                                'social-media-posts',
                                            )}
                                            onMouseEnter={() =>
                                                setIsHoveringTableLink('1')
                                            }
                                            onMouseLeave={() =>
                                                setIsHoveringTableLink(null)
                                            }
                                            style={{
                                                color:
                                                    isHoveringTableLink === '1'
                                                        ? colorPrimary
                                                        : undefined,
                                            }}
                                        >
                                            {t(
                                                'dataExplorer.table.viewSocialPosts',
                                            )}
                                        </a>
                                    ),
                                    disabled: !postCount,
                                    icon: (
                                        <i
                                            className="fa-regular fa-images"
                                            style={{
                                                color:
                                                    isHoveringTableLink === '1'
                                                        ? colorPrimary
                                                        : undefined,
                                            }}
                                        />
                                    ),
                                },
                                {
                                    key: '2',
                                    label: (
                                        <a
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={_showRelevant(
                                                'broadcasts/exposures',
                                            )}
                                            onMouseEnter={() =>
                                                setIsHoveringTableLink('2')
                                            }
                                            onMouseLeave={() =>
                                                setIsHoveringTableLink(null)
                                            }
                                            style={{
                                                color:
                                                    isHoveringTableLink === '2'
                                                        ? colorPrimary
                                                        : undefined,
                                            }}
                                        >
                                            {t(
                                                'dataExplorer.table.viewBroadcastFrames',
                                            )}
                                        </a>
                                    ),
                                    disabled: !broadcastCount,

                                    icon: (
                                        <i
                                            className="fa-solid fa-tv-retro"
                                            style={{
                                                color:
                                                    isHoveringTableLink === '2'
                                                        ? colorPrimary
                                                        : undefined,
                                            }}
                                        />
                                    ),
                                },
                            ];

                            return (
                                <Dropdown menu={{ items }}>
                                    <Space size="middle">
                                        <a
                                            style={{
                                                padding: 2,
                                                display: 'inline-block',
                                                cursor: 'pointer',
                                            }}
                                        >
                                            <i
                                                className="fa-solid fa-ellipsis-vertical"
                                                style={{
                                                    fontSize: 16,
                                                    color: colorPrimary,
                                                }}
                                            />
                                        </a>
                                    </Space>
                                </Dropdown>
                            );
                        }

                        if (hasCompareDates) {
                            if (Header === 'Difference') {
                                return (
                                    <Tag
                                        color={value > 0 ? 'success' : 'error'}
                                    >
                                        {formatter(value)}
                                    </Tag>
                                );
                            }
                            if (Header === 'Percentage Change') {
                                return (
                                    <span
                                        style={{
                                            color:
                                                value > 0
                                                    ? colorSuccess
                                                    : value < 0
                                                      ? colorError
                                                      : null,
                                        }}
                                    >
                                        {formatter(value)}
                                    </span>
                                );
                            }
                        }
                        if (typeof formatter === 'function') {
                            return formatter(value);
                        }

                        return value;
                    },

                    loading: {
                        spinning: loading,
                        size: 'large',
                        delay: 300,
                    },
                };
            },
            [columnsRaw, client, isLoading],
        );

        const onChange = (pagination, filters, sorter) => {
            if (Array.isArray(sorter)) return;

            const { field: metric, order } = sorter;

            if (metric === 'redirect') return;

            if (!(metric in METRIC_TYPES)) {
                setIsSelectingFromTable(true);
                setTableSelected(metric);
            } else {
                setIsSelectingFromTable(false);
                setSelectedMetric(metric);
                setSortingOrder(
                    order === 'descend'
                        ? SORTING_ORDER.DESC
                        : SORTING_ORDER.ASC,
                );
            }
        };

        return {
            rowKey: (record, index) => {
                // Insights rows can share/miss `id`; build a deterministic key
                // from common grouping fields and keep index as a uniqueness fallback.
                const keyBase = [
                    record?.id,
                    record?.rightsholderName,
                    record?.rightsholderPlacementName,
                    record?.placementTypeName,
                    record?.accountUsername,
                    record?.collectionName,
                    record?.gameName,
                ]
                    .filter(v => v !== undefined && v !== null && v !== '')
                    .join('|');

                return keyBase ? `${keyBase}::${index}` : `row::${index}`;
            },
            columns,
            dataSource: data,
            loading,
            pagination: {
                current: page + 1,
                pageSize: hasCompareDates ? DATA_COMPARING_PAGE_SIZE : 100,
                onChange: newPage => setPage(newPage - 1),
            },
            onChange,
            scroll: { y: '60vh' },
            locale: {
                emptyText: (
                    <NoDataMessage
                        isLoading={
                            hasCompareDates ? isCompareLoading : isLoading
                        }
                        isResponseLengthTooLong={
                            hasCompareDates
                                ? hasTooManyRowsForCompare
                                : hasTooManyRows
                        }
                    />
                ),
            },
        };
    }, [
        data,
        hasCompareDates,
        isCompareLoading,
        isLoading,
        renderColumns,
        renderCompareColumns,
        page,
        hasTooManyRows,
        hasTooManyRowsForCompare,
    ]);

    const {
        token: { colorPrimary, colorError, colorSuccess },
    } = theme.useToken();

    const summary = useCallback(() => {
        return (
            <Table.Summary.Row>
                {antdTableProps.columns?.map((column, index) => {
                    const { dataIndex: accessor, render } = column;

                    if (index === 0) {
                        return (
                            <Table.Summary.Cell
                                index={index}
                                key={column.key}
                                className="summary-cell first-cell"
                            >
                                {t('dataExplorer.table.total')}
                            </Table.Summary.Cell>
                        );
                    }

                    if (index === antdTableProps.columns.length - 1) {
                        return (
                            <Table.Summary.Cell
                                index={index}
                                key={column.key}
                                className="summary-cell last-cell"
                            />
                        );
                    }

                    const fullData = antdTableProps.dataSource || [];
                    const values = fullData.map(row => row[accessor]);
                    const filtered = values.filter(
                        v => v !== undefined && v !== null,
                    );

                    if (filtered.length === 0) {
                        return (
                            <Table.Summary.Cell
                                index={index}
                                key={column.key}
                                className="summary-cell"
                            />
                        );
                    }

                    const firstValue = filtered[0];
                    let summaryValue: any = null;

                    // 🟢 If it's a number → total
                    if (typeof firstValue === 'number') {
                        const total = filtered.reduce(
                            (sum, val) =>
                                typeof val === 'number' ? sum + val : sum,
                            0,
                        );

                        if (
                            column.title === 'MVP' ||
                            (hasCompareDates && compareMetric === 'averageMvp')
                        ) {
                            const avg = (total / filtered.length) * 100;

                            return (
                                <Table.Summary.Cell
                                    index={index}
                                    key={column.key}
                                    className="summary-cell"
                                >
                                    {`${avg.toFixed(1)}%`}
                                </Table.Summary.Cell>
                            );
                        } else if (column.title === 'Difference') {
                            const { format: metricFormat } =
                                yAxisMap[compareMetric];

                            return (
                                <Table.Summary.Cell
                                    index={index}
                                    key={column.key}
                                    className="summary-cell"
                                >
                                    {metricFormat(total)}
                                </Table.Summary.Cell>
                            );
                        } else if (column.title === 'Percentage Change') {
                            const [totalCompare, totalDifference] =
                                compareValues.reduce(
                                    (acc, row) => {
                                        acc[0] += row.compareDateRange;
                                        acc[1] += row.difference;
                                        return acc;
                                    },
                                    [0, 0],
                                );
                            const percentageChange = totalCompare
                                ? totalDifference / totalCompare
                                : 'N/A';

                            return (
                                <Table.Summary.Cell
                                    index={index}
                                    key={column.key}
                                    className="summary-cell"
                                >
                                    {percentageChange === 'N/A'
                                        ? percentageChange
                                        : format.percentage(percentageChange)}
                                </Table.Summary.Cell>
                            );
                        } else {
                            summaryValue = total;
                        }
                    } else {
                        summaryValue = null;
                    }

                    const renderedSummary =
                        summaryValue === null
                            ? null
                            : typeof render === 'function'
                              ? render(summaryValue, {})
                              : summaryValue;

                    return (
                        <Table.Summary.Cell
                            index={index}
                            key={column.key}
                            className={`summary-cell`}
                        >
                            {renderedSummary}
                        </Table.Summary.Cell>
                    );
                })}
            </Table.Summary.Row>
        );
    }, [
        antdTableProps.columns,
        antdTableProps.dataSource,
        colorPrimary,
        isLoading,
        hasCompareDates,
        hasTooManyRows,
        hasTooManyRowsForCompare,
        isCompareLoading,
        compareMetric,
        yAxisMap,
    ]);

    return (
        <Card
            style={{
                marginTop: 24,
            }}
            ref={cardRef}
        >
            <Flex
                align="center"
                style={{
                    marginBottom: 24,
                }}
            >
                <Flex flex={1}>
                    <Typography.Title
                        level={4}
                        style={{
                            margin: 0,
                            color: '#1f1f1f',
                            fontWeight: 400,
                        }}
                    >
                        {t('dataExplorer.table.title')}
                    </Typography.Title>
                </Flex>

                <Flex>
                    <div>
                        <Button
                            onClick={_openSelectingColumns}
                            data-test="selected-metrics"
                            color="primary"
                            variant="outlined"
                            disabled={isLoading || hasCompareDates}
                            style={{
                                padding: 8,
                            }}
                        >
                            <i className="fa-regular fa-gear" />
                        </Button>

                        {isSelectingColumns && (
                            <SelectingMetrics
                                insightsColumns={insightsColumns}
                                columns={columns}
                                setColumns={_setColumns}
                                onClose={() => setIsSelectingColumns(false)}
                            />
                        )}
                    </div>
                    <Button
                        onClick={_downloadAntdTableCsv}
                        className="-m-l-2"
                        data-test="insights-download"
                        type="primary"
                        color="primary"
                        variant="solid"
                        disabled={isLoading}
                    >
                        <i className="fa-regular fa-download -m-r-1" />
                        {t('dataExplorer.table.downloadCsv')}
                    </Button>
                </Flex>
            </Flex>
            <Row gutter={16}>
                <Col span={24}>
                    <div className="data-explorer-table">
                        <ErrorBoundary FallbackComponent={CrashedComponent}>
                            <ResizableTable
                                ref={antdTable}
                                {...antdTableProps}
                                pagination={{
                                    pageSize: 10,
                                    showSizeChanger: false,
                                }}
                                locale={{
                                    emptyText: loading ? (
                                        <Skeleton
                                            active
                                            paragraph={{ rows: 10 }}
                                        />
                                    ) : (
                                        antdTableProps.locale.emptyText
                                    ),
                                }}
                                bordered
                                rowClassName={(_, index) =>
                                    index % 2 === 0
                                        ? 'table-row-light'
                                        : 'table-row-dark'
                                }
                                summary={summary}
                                showSorterTooltip={false}
                            />
                        </ErrorBoundary>
                    </div>
                </Col>
            </Row>
        </Card>
    );
};

export default connect(
    ['insights', 'filters', 'user', 'client'],
    combineActions({
        overrideFilters,
        sendNotification,
    }),
)(Insights);
